"use client";

import React, { useState } from "react";
import { qaApi } from "@/lib/api/services/qa.api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Save, Loader2, HelpCircle } from "lucide-react";
import Link from "next/link";
import { toast } from "@/components/ui/toast";

export default function QaAskPage() {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [tags, setTags] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!title.trim() || !content.trim()) {
      toast({ type: "error", title: "Validation Error", message: "Title and details are required." });
      return;
    }
    setIsSaving(true);
    try {
      const tagList = tags.split(",").map((t) => t.trim()).filter(Boolean);
      await qaApi.ask({ title, content, tags: tagList });
      toast({ type: "success", title: "Question posted", message: "Your question has been submitted." });
      setTitle("");
      setContent("");
      setTags("");
    } catch {
      toast({ type: "error", title: "Error", message: "Failed to post question. Please try again." });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" asChild>
          <Link href="/qa">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Link>
        </Button>
      </div>

      <h1 className="text-2xl font-bold text-foreground">Ask a Question</h1>

      <Card>
        <CardContent className="p-6 space-y-4">
          <div>
            <label htmlFor="qa-title" className="block text-sm font-medium text-foreground mb-1">Title</label>
            <input
              id="qa-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground"
              placeholder="Enter your question"
            />
          </div>
          <div>
            <label htmlFor="qa-details" className="block text-sm font-medium text-foreground mb-1">Details</label>
            <textarea
              id="qa-details"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={10}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground"
              placeholder="Provide detailed context for your question"
            />
          </div>
          <div>
            <label htmlFor="qa-tags" className="block text-sm font-medium text-foreground mb-1">Tags (comma-separated)</label>
            <input
              id="qa-tags"
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground"
              placeholder="e.g. hr, recruitment, compliance"
            />
          </div>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Post Question
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}